import React from 'react';
import { ArrowLeft, Terminal, BookOpen, Search, Sparkles } from 'lucide-react';
import { CodeBlock } from './CodeBlock';
import TerminalCast from './TerminalCast';
import {
  sections,
  methodColor,
  authLabel,
  slugForEndpoint,
  docSectionById,
  type DocSection,
  type Endpoint,
} from './docsData';

// Real API reference for app.nz. The endpoint data lives in ./docsData (shared
// with the per-endpoint deep-link pages at /docs/{slug} and the sitemap). Each
// callable row links to its own documentation page. Each section also has a
// crawlable subpage at /docs/{section.id}.

const BUILD_PROMPT =
  'Read https://app.nz/llms-full.txt and follow it to build an agent on app.nz: ' +
  'call the OpenAI-compatible gateway, launch a cloud coding agent, and ship a deploy.';

const SWITCH_SNIPPET = `from openai import OpenAI

# Same OpenAI code. Only the base URL and key change.
client = OpenAI(base_url="https://app.nz/v1", api_key="pk_live_...")
r = client.chat.completions.create(
    model="app/auto",  # router picks the model, or pin "provider/model"
    messages=[{"role": "user", "content": "Hello from app.nz"}],
)`;

const AiAgentCard: React.FC = () => (
  <section className="mb-6 min-w-0 rounded-xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] p-5">
    <div className="mb-3 flex items-center gap-2">
      <Sparkles size={18} className="text-white/70" />
      <h2 className="font-display text-lg font-bold">Use these docs with your AI agent</h2>
    </div>
    <p className="mb-4 max-w-3xl text-sm leading-7 text-white/58">
      Point a coding agent at{' '}
      <a href="/llms.txt" className="font-mono text-white underline decoration-white/30 hover:decoration-white">/llms.txt</a>{' '}
      for the index, or{' '}
      <a href="/llms-full.txt" className="font-mono text-white underline decoration-white/30 hover:decoration-white">/llms-full.txt</a>{' '}
      for the full guide. Paste this prompt into any agent:
    </p>
    <div className="grid min-w-0 gap-4 lg:grid-cols-2">
      <div className="min-w-0">
        <div className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-white/34">Build-an-agent prompt</div>
        <CodeBlock code={BUILD_PROMPT} preClassName="!text-[13px] !leading-6 text-white/80" />
      </div>
      <div className="min-w-0">
        <div className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-white/34">Already have OpenAI code?</div>
        <CodeBlock code={SWITCH_SNIPPET} language="python" preClassName="!text-[13px] !leading-6" />
      </div>
    </div>
  </section>
);

const EndpointRow: React.FC<{ endpoint: Endpoint }> = ({ endpoint: e }) => {
  const slug = slugForEndpoint(e.method, e.path);
  const inner = (
    <>
      <span
        className={`inline-flex w-16 shrink-0 justify-center rounded border px-2 py-0.5 text-xs font-bold ${methodColor[e.method]}`}
      >
        {e.method}
      </span>
      <code className="min-w-0 break-all font-mono text-sm text-white/80">{e.path}</code>
      <span className="flex w-full min-w-0 items-center gap-3 sm:ml-auto sm:w-auto">
        <span className="min-w-0 break-words text-sm text-white/48">{e.summary}</span>
        {e.auth && (
          <span className="shrink-0 rounded border border-white/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/40">
            {authLabel[e.auth]}
          </span>
        )}
      </span>
    </>
  );

  if (!slug) {
    return (
      <div className="flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-white/10 bg-black px-3 py-2">
        {inner}
      </div>
    );
  }

  return (
    <a
      href={`/docs/${slug}`}
      className="flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-white/10 bg-black px-3 py-2 transition-colors hover:border-white/25 hover:bg-white/[0.04]"
    >
      {inner}
    </a>
  );
};

function filterSections(all: DocSection[], query: string): DocSection[] {
  const q = query.trim().toLowerCase();
  if (!q) return all;
  const out: DocSection[] = [];
  for (const s of all) {
    const sectionHit =
      s.label.toLowerCase().includes(q) || s.title.toLowerCase().includes(q) || s.intro.toLowerCase().includes(q);
    const endpoints = s.endpoints?.filter(
      (e) => e.path.toLowerCase().includes(q) || e.summary.toLowerCase().includes(q) || e.method.toLowerCase() === q,
    );
    const paramHit = s.params?.rows.some((p) => p.name.toLowerCase().includes(q) || p.desc.toLowerCase().includes(q));
    const exampleHit = s.example?.code.toLowerCase().includes(q) || s.example?.label.toLowerCase().includes(q);
    if (sectionHit || paramHit || exampleHit || (endpoints && endpoints.length > 0)) {
      // When only an endpoint matches, keep the result focused. A section-level
      // match intentionally keeps the whole section visible for product terms.
      out.push(sectionHit || paramHit || exampleHit ? s : { ...s, endpoints });
    }
  }
  return out;
}

function exampleLanguage(code: string): string {
  if (/^\s*curl\b/m.test(code) || /^\s*#/.test(code) || /^\s*app /.test(code)) return 'bash';
  return '';
}

const DocsSectionBody: React.FC<{ section: DocSection; headingHref?: string; hideHeading?: boolean }> = ({
  section,
  headingHref,
  hideHeading,
}) => {
  const heading = hideHeading ? null : (
    <div className="mb-2 flex items-center gap-3">
      <section.icon size={18} className="text-white/54" />
      <h2 className="font-display text-xl font-bold">
        {headingHref ? (
          <a href={headingHref} className="hover:underline">
            {section.title}
          </a>
        ) : (
          section.title
        )}
      </h2>
    </div>
  );
  return (
    <>
      {heading}
      <p className="mb-4 max-w-3xl text-sm leading-7 text-white/58">{section.intro}</p>

      {section.baseUrl && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-white/10 bg-[#050505] p-3 font-mono text-sm">
          <span className="text-xs font-bold uppercase tracking-[0.16em] text-white/34">base</span>
          <span className="text-white/72">{section.baseUrl}</span>
        </div>
      )}

      {section.endpoints && (
        <div className="mb-4 grid gap-2">
          {section.endpoints.map((e) => (
            <EndpointRow key={e.method + e.path} endpoint={e} />
          ))}
        </div>
      )}

      {section.params && (
        <div className="mb-4 overflow-hidden rounded-lg border border-white/10">
          <div className="border-b border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white/40">
            {section.params.heading}
          </div>
          <div className="divide-y divide-white/[0.06]">
            {section.params.rows.map((p) => (
              <div key={p.name} className="grid gap-1 px-3 py-2 sm:grid-cols-[0.28fr_0.14fr_0.58fr] sm:items-baseline">
                <code className="font-mono text-sm text-white/80">
                  {p.name}
                  {p.required && <span className="ml-1 text-rose-300/80">*</span>}
                </code>
                <span className="font-mono text-xs text-white/40">{p.type}</span>
                <span className="text-sm text-white/54">{p.desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {section.example && (
        <div>
          <div className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-white/34">{section.example.label}</div>
          <CodeBlock
            code={section.example.code}
            language={exampleLanguage(section.example.code)}
            preClassName="!text-[13px] !leading-6"
          />
        </div>
      )}
    </>
  );
};

function DocsNav({ activeId, query, onQuery }: { activeId?: string; query: string; onQuery: (v: string) => void }) {
  const visible = React.useMemo(() => filterSections(sections, query), [query]);
  return (
    <>
      <label className="mb-3 flex items-center gap-2 rounded-lg border border-white/10 bg-black px-2.5 py-2">
        <Search size={14} className="shrink-0 text-white/40" />
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search docs, endpoints, examples…"
          className="w-full bg-transparent text-sm text-white placeholder-white/35 outline-none"
        />
      </label>
      <div className="mb-2 px-2 text-xs font-bold uppercase tracking-[0.2em] text-white/34">
        {activeId ? 'API sections' : 'On this page'}
      </div>
      <div className="grid gap-1">
        {visible.map((s) => {
          const Icon = s.icon;
          const href = `/docs/${s.id}`;
          const active = s.id === activeId;
          return (
            <a
              key={s.id}
              href={href}
              className={`flex items-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors hover:bg-white/[0.06] hover:text-white ${active ? 'bg-white/[0.08] text-white' : 'text-white/58'}`}
            >
              <Icon size={15} />
              {s.label}
            </a>
          );
        })}
        <a
          href="/docs/architecture.md"
          className="mt-1 flex items-center gap-2 rounded-lg px-2 py-2 text-sm text-white/40 transition-colors hover:bg-white/[0.06] hover:text-white"
        >
          <BookOpen size={15} />
          Architecture
        </a>
      </div>
    </>
  );
}

export const DocsSectionPage: React.FC<{ sectionId: string }> = ({ sectionId }) => {
  const section = docSectionById(sectionId);
  const [query, setQuery] = React.useState('');
  if (!section) return null;
  return (
    <main className="min-h-screen bg-black px-4 pb-16 pt-24 text-white sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1800px] min-w-0">
        <nav className="mb-5 flex flex-wrap items-center gap-2 text-sm font-semibold text-white/48">
          <a href="/docs" className="inline-flex items-center gap-2 hover:text-white">
            <ArrowLeft size={16} />
            API reference
          </a>
          <span className="text-white/24">/</span>
          <span className="text-white/70">{section.title}</span>
        </nav>

        <div className="mb-6 grid gap-6 lg:grid-cols-[0.22fr_0.78fr]">
          <nav className="top-24 hidden h-fit rounded-xl border border-white/10 bg-white/[0.035] p-3 lg:sticky lg:block">
            <DocsNav activeId={section.id} query={query} onQuery={setQuery} />
          </nav>
          <section
            data-testid={`docs-section-${section.id}`}
            className="min-w-0 rounded-xl border border-white/10 bg-white/[0.035] p-5"
          >
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white/54">
              <Terminal size={14} />
              {section.label}
            </div>
            <h1 className="mb-4 font-display text-2xl font-bold leading-tight tracking-tight sm:text-4xl">{section.title}</h1>
            <DocsSectionBody section={section} hideHeading />
          </section>
        </div>
      </div>
    </main>
  );
};

export const PlatformDocs: React.FC = () => {
  const [query, setQuery] = React.useState('');
  const visible = React.useMemo(() => filterSections(sections, query), [query]);

  React.useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (hash && docSectionById(hash)) {
      window.history.replaceState({}, '', `/docs/${hash}`);
    }
  }, []);

  return (
    <main className="min-h-screen bg-black px-4 pb-16 pt-24 text-white sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1800px] min-w-0">
        <a href="/" className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-white/48 hover:text-white">
          <ArrowLeft size={16} />
          Back to overview
        </a>

        <div className="mb-6 grid gap-5 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white/54">
              <Terminal size={14} />
              api reference
            </div>
            <h1 className="font-display text-2xl font-bold leading-tight tracking-tight sm:text-4xl">
              One key. Agents, models, deploys.
            </h1>
          </div>
          <p className="text-sm leading-7 text-white/58">
            Launch coding agents on your repos, route chat across every major model, and publish sites. Each
            endpoint links to its live reference.
          </p>
        </div>

        <AiAgentCard />
        <div className="mb-6 grid items-center gap-5 rounded-xl border border-white/10 bg-white/[0.025] p-5 lg:grid-cols-[0.34fr_0.66fr]">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-cyan-200/55">CLI + MCP handoff</p>
            <h2 className="mt-2 font-display text-xl font-bold">Give any LLM the same platform context.</h2>
            <p className="mt-2 text-sm leading-6 text-white/52">Generate a deployment brief offline, or connect the app.nz MCP server so an agent can retrieve instructions and call platform tools.</p>
          </div>
          <TerminalCast src="/casts/cli-llm-handoff.cast" title="app: hand the platform to any LLM" rows={15} />
        </div>

        <div className="grid min-w-0 gap-6 lg:grid-cols-[0.22fr_0.78fr]">
          <nav className="top-24 hidden h-fit rounded-xl border border-white/10 bg-white/[0.035] p-3 lg:sticky lg:block">
            <DocsNav query={query} onQuery={setQuery} />
          </nav>

          <div className="grid min-w-0 gap-5">
            <div className="lg:hidden">
              <label className="flex items-center gap-2 rounded-lg border border-white/10 bg-black px-2.5 py-2">
                <Search size={14} className="shrink-0 text-white/40" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search docs, endpoints, examples…"
                  className="w-full bg-transparent text-sm text-white placeholder-white/35 outline-none"
                />
              </label>
            </div>
            {visible.length === 0 && (
              <div className="rounded-xl border border-white/10 bg-white/[0.035] p-5 text-sm text-white/55">
                Nothing matches “{query}”. Try an endpoint path (e.g. <code className="text-white/75">/api/apps</code>), a
                method, or a product name, or ask in <a href="/chat" className="text-white/80 underline">chat</a>.
              </div>
            )}
            {visible.map((section) => (
              <section
                key={section.id}
                data-testid={`docs-section-${section.id}`}
                className="min-w-0 rounded-xl border border-white/10 bg-white/[0.035] p-5"
              >
                <DocsSectionBody section={section} headingHref={`/docs/${section.id}`} />
              </section>
            ))}

            <section className="rounded-xl border border-white/10 bg-white/[0.035] p-5">
              <h2 className="font-display text-lg font-bold">Conventions</h2>
              <ul className="mt-3 grid gap-2 text-sm leading-6 text-white/54">
                <li>• Auth: <code className="text-white/72">Authorization: Bearer pk_live_...</code> on every non-public route.</li>
                <li>• Bodies and responses are JSON; successful writes return <code className="text-white/72">{'{ "success": true, ... }'}</code>.</li>
                <li>• Errors return a non-2xx status with <code className="text-white/72">{'{ "error": "message" }'}</code>.</li>
                <li>• Get a key with the CLI: <code className="text-white/72">app keys create --name "deploy bot"</code>.</li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </main>
  );
};
